import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  RiStackLine,
  RiTeamLine,
  RiFlowChart,
  RiPlayCircleLine,
  RiRefreshLine,
  RiFlaskLine,
  RiListCheck3,
  RiShareLine,
  RiStopCircleLine,
  RiZoomInLine,
  RiZoomOutLine,
  RiMessage2Line,
  RiPauseLine,
  RiRepeat2Line,
  RiToggleLine,
  RiCloseLine,
  RiFileTextLine,
  RiTimerLine,
  RiSkipBackLine,
  RiSkipForwardLine,
  RiErrorWarningLine,
} from 'react-icons/ri';
import { useInjectCSS, SectionLabel, ExplainerRow } from './diagrams/tutorialHelpers';
import { AskDemoPanel } from './diagrams/AskDemoPanel';
import { SwarmDemoPanel } from './diagrams/SwarmDemoPanel';
import { WorkflowChatDemoPanel, WorkflowExplainer, useWfNodeCSS } from './diagrams/WorkflowChatDemoPanel';

// ── Explainer sections ────────────────────────────────────────────────────────

function AskExplainer() {
  return (
    <div>
      <SectionLabel>Agents</SectionLabel>
      <ExplainerRow
        Icon={RiStackLine} iconColor="#636cff"
        label="Orchestrator"
        description="Your primary point of contact. Immediately answers from its own knowledge, then plans and routes tasks to specialist agents. Synthesizes everything into a final report."
      />
      <ExplainerRow
        Icon={RiShareLine} iconColor="#0ea5e9"
        label="Generalist Agent"
        description="Searches the web for current events, news, and general knowledge. Runs in the background while you read the preliminary answer."
      />
      <ExplainerRow
        Icon={RiFlaskLine} iconColor="#7c3aed"
        label="Researcher Agent"
        description="Searches academic databases and scientific literature for peer-reviewed papers and evidence-based analysis."
      />
      <ExplainerRow
        Icon={RiListCheck3} iconColor="#f59e0b"
        label="Evaluator Agent"
        description="An independent quality auditor. Triggered on-demand to critique the accuracy, completeness, and reasoning of any response."
      />
      <ExplainerRow
        Icon={RiErrorWarningLine} iconColor="#e11d48"
        label="Challenger Agent"
        description="A constructive critic. Stress-tests claims, exposes hidden assumptions, and surfaces the strongest counter-argument — assignable in Swarm debates and workflow nodes."
      />

      <SectionLabel>On-demand actions (on any response)</SectionLabel>
      <ExplainerRow
        Icon={RiZoomOutLine} iconColor="#636cff"
        label="Concise"
        description="Shows the Orchestrator's immediate preliminary answer — drawn from its own knowledge and available in seconds, before any background research runs."
      />
      <ExplainerRow
        Icon={RiZoomInLine} iconColor="#7c3aed"
        label="Expansive"
        description="Shows the fully synthesized final report combining the Orchestrator, Generalist, and Researcher. If not yet generated, triggers a full agentic deep-dive from scratch."
      />
      <ExplainerRow
        Icon={RiListCheck3} iconColor="#f59e0b"
        label="Evaluation"
        description="Sends the response to the Evaluator agent for a structured critique: accuracy, depth, unsupported claims, and gaps."
      />

      <SectionLabel>Live control</SectionLabel>
      <ExplainerRow
        Icon={RiStopCircleLine} iconColor="#e53935"
        label="Stop Thinking"
        description="Stop background research at any time and keep the current preliminary answer. Use this when the first response is already sufficient."
      />
    </div>
  );
}

function SwarmExplainer() {
  return (
    <div>
      <SectionLabel>How Swarm works</SectionLabel>
      <ExplainerRow
        Icon={RiTeamLine} iconColor="#28a745"
        label="Agent turns"
        description="Multiple agents take turns contributing perspectives, building on or pushing back against each other. Each contribution appears as it streams in."
      />
      <ExplainerRow
        Icon={RiRepeat2Line} iconColor="#636cff"
        label="Turns"
        description="Set the maximum number of agent contributions before the swarm stops automatically. Increase for deeper debates; lower for a focused exchange."
      />

      <SectionLabel>Controls during a debate</SectionLabel>
      <ExplainerRow
        Icon={RiMessage2Line} iconColor="#636cff"
        label="Intervene"
        description="Inject new context, ask a follow-up question, or redirect the topic mid-debate. Agents incorporate your input immediately in their next turn."
      />
      <ExplainerRow
        Icon={RiPauseLine} iconColor="#f59e0b"
        label="Pause / Resume"
        description="Suspend the debate at any point to read and reflect, then continue when you're ready."
      />
      <ExplainerRow
        Icon={RiToggleLine} iconColor="#7c3aed"
        label="Pause on Turn"
        description="Enable this toggle to have the swarm stop automatically after every single agent contribution, giving you full turn-by-turn control over the direction."
      />
      <ExplainerRow
        Icon={RiTimerLine} iconColor="#0ea5e9"
        label="Delay"
        description="Adjust the pacing between agent turns. Increase it if the contributions are appearing faster than you can read them."
      />

      <SectionLabel>Ending a session</SectionLabel>
      <ExplainerRow
        Icon={RiFileTextLine} iconColor="#28a745"
        label="Summarize"
        description="At any point (after at least one agent has spoken), request a conclusive report that synthesizes the entire debate into a structured final answer with key arguments and conclusions."
      />
      <ExplainerRow
        Icon={RiCloseLine} iconColor="#e53935"
        label="Stop"
        description="End the debate immediately without generating a summary. The transcript remains visible in the conversation."
      />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

// Milliseconds between consecutive steps for each mode (used to resume from any step).
const STEP_INTERVALS = {
  ask:      [400, 800, 1400, 800, 2400, 1000, 2200, 400],
  swarm:    [500, 2300, 2600, 2400, 2000, 400],
  workflow: [600, 1000, 1200, 1200, 2500, 400],
};

const TutorialContent = ({ onDismiss, onRestore, showAtStartup }) => {
  useInjectCSS();
  useWfNodeCSS();

  const [activeTab, setActiveTab] = useState('ask');
  const [askStep, setAskStep] = useState(0);
  const [swarmStep, setSwarmStep] = useState(0);
  const [workflowStep, setWorkflowStep] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const timersRef = useRef([]);
  const demoScrollRef = useRef(null);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const after = useCallback((fn, ms) => {
    timersRef.current.push(setTimeout(fn, ms));
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  // Auto-scroll to bottom whenever any step advances (including workflow).
  useEffect(() => {
    if (demoScrollRef.current) {
      demoScrollRef.current.scrollTo({ top: demoScrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [askStep, swarmStep, workflowStep]);

  const setStep = useCallback((tab, val) => {
    if (tab === 'ask') setAskStep(val);
    else if (tab === 'swarm') setSwarmStep(val);
    else setWorkflowStep(val);
  }, []);

  // Play from a given starting step using relative intervals.
  const playFrom = useCallback((tab, startStep) => {
    const intervals = STEP_INTERVALS[tab];
    const maxStep = intervals.length - 1;
    clearTimers();
    setIsRunning(true);
    setIsPaused(false);
    let elapsed = 0;
    for (let s = startStep + 1; s <= maxStep; s++) {
      elapsed += intervals[s - 1];
      const target = s;
      after(() => setStep(tab, target), elapsed);
    }
    after(() => setIsRunning(false), elapsed + 400);
  }, [clearTimers, after, setStep]);

  const runAsk      = useCallback(() => { setAskStep(0);      playFrom('ask', 0); }, [playFrom]);
  const runSwarm    = useCallback(() => { setSwarmStep(0);    playFrom('swarm', 0); }, [playFrom]);
  const runWorkflow = useCallback(() => { setWorkflowStep(0); playFrom('workflow', 0); }, [playFrom]);

  const handlePlay = () => {
    if (activeTab === 'ask') runAsk();
    else if (activeTab === 'swarm') runSwarm();
    else runWorkflow();
  };

  const handleReset = () => {
    clearTimers();
    setIsRunning(false);
    setIsPaused(false);
    setStep(activeTab, 0);
  };

  const handlePause = () => {
    clearTimers();
    setIsRunning(false);
    setIsPaused(true);
  };

  const handleResume = () => {
    const cur = activeTab === 'ask' ? askStep : activeTab === 'swarm' ? swarmStep : workflowStep;
    const max = STEP_INTERVALS[activeTab].length - 1;
    if (cur >= max) { setIsPaused(false); return; }
    playFrom(activeTab, cur);
  };

  const handleStepBack = () => {
    clearTimers();
    setIsRunning(false);
    setIsPaused(true);
    const cur = activeTab === 'ask' ? askStep : activeTab === 'swarm' ? swarmStep : workflowStep;
    setStep(activeTab, Math.max(0, cur - 1));
  };

  const handleStepForward = () => {
    clearTimers();
    setIsRunning(false);
    setIsPaused(true);
    const max = STEP_INTERVALS[activeTab].length - 1;
    const cur = activeTab === 'ask' ? askStep : activeTab === 'swarm' ? swarmStep : workflowStep;
    setStep(activeTab, Math.min(max, cur + 1));
  };

  const handleTabChange = (tab) => {
    clearTimers();
    setIsRunning(false);
    setIsPaused(false);
    setAskStep(0);
    setSwarmStep(0);
    setWorkflowStep(0);
    setActiveTab(tab);
  };

  const currentStep = activeTab === 'ask' ? askStep : activeTab === 'swarm' ? swarmStep : workflowStep;
  const maxStep = STEP_INTERVALS[activeTab].length - 1;
  const isDone = currentStep >= maxStep;
  const isIdle = currentStep === 0 && !isPaused;
  const accentColor = activeTab === 'ask' ? '#636cff' : activeTab === 'swarm' ? '#28a745' : '#f59e0b';

  return (
    <div style={{ marginTop: '1rem', lineHeight: '1.6', fontSize: '0.95rem' }}>
      <h2 style={{ borderBottom: '1px solid var(--panel-border, #ccc)', paddingBottom: '0.5rem' }}>
        Interactive Tutorial
      </h2>
      <p style={{ marginBottom: '1.5rem' }}>
        Step through live simulations of all three modes, then read the explainer
        below for details.
      </p>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {[
          { id: 'ask',      label: 'Ask',       Icon: RiStackLine,  color: '#636cff' },
          { id: 'swarm',    label: 'Swarm',     Icon: RiTeamLine,   color: '#28a745' },
          { id: 'workflow', label: 'Workflow',  Icon: RiFlowChart,  color: '#f59e0b' },
        ].map(({ id, label, Icon, color }) => (
          <button
            key={id}
            onClick={() => handleTabChange(id)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '0.45rem 1rem',
              border: `2px solid ${activeTab === id ? color : 'var(--border-color, #ddd)'}`,
              borderRadius: '8px',
              backgroundColor: activeTab === id ? `${color}18` : 'transparent',
              color: activeTab === id ? color : 'var(--text-secondary, #666)',
              fontWeight: activeTab === id ? 700 : 400,
              cursor: 'pointer', fontSize: '0.9rem', transition: 'all 0.18s',
            }}
          >
            <Icon size={17} /> {label}
          </button>
        ))}
      </div>

      {/* Simulation viewport */}
      <div style={{
        border: '1px solid var(--border-color, #ddd)',
        borderRadius: '12px', overflow: 'hidden',
        backgroundColor: 'var(--bg-primary, #fff)',
        display: 'flex', flexDirection: 'column', height: '340px',
      }}>
        {/* Mock title bar */}
        <div style={{
          padding: '0.45rem 1rem',
          borderBottom: '1px solid var(--border-color, #ddd)',
          backgroundColor: 'var(--bg-secondary, #f5f5f5)',
          display: 'flex', alignItems: 'center', gap: '6px',
          fontSize: '0.78rem', color: 'var(--text-secondary, #888)',
          userSelect: 'none',
        }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#ff5f57', display: 'inline-block' }} />
          <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#ffbd2e', display: 'inline-block' }} />
          <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#28c940', display: 'inline-block' }} />
          <span style={{ marginLeft: '6px' }}>
            Stackform —{' '}
            {activeTab === 'ask'
              ? <><RiStackLine style={{ verticalAlign: 'middle', marginRight: 4 }} /> Ask Mode (Orchestration)</>
              : activeTab === 'swarm'
              ? <><RiTeamLine style={{ verticalAlign: 'middle', marginRight: 4 }} /> Swarm Mode (Open Forum)</>
              : <><RiFlowChart style={{ verticalAlign: 'middle', marginRight: 4 }} /> Workflow Mode</>
            }
          </span>
        </div>

        {/* Message area */}
        <div
          ref={demoScrollRef}
          style={{
            flex: 1, padding: '1rem',
            display: 'flex', flexDirection: 'column', gap: '0.5rem',
            overflowY: 'auto',
          }}
        >
          {isIdle ? (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#888', fontSize: '0.88rem', fontStyle: 'italic',
            }}>
              Press <strong style={{ margin: '0 4px', color: 'inherit' }}>Start</strong> below to start the simulation
            </div>
          ) : activeTab === 'ask' ? (
            <AskDemoPanel step={askStep} />
          ) : activeTab === 'swarm' ? (
            <SwarmDemoPanel step={swarmStep} />
          ) : (
            <WorkflowChatDemoPanel step={workflowStep} />
          )}
        </div>

        {/* Controls bar — two fixed rows so buttons never shift position */}
        <div style={{
          padding: '0.5rem 1rem',
          borderTop: '1px solid var(--border-color, #ddd)',
          backgroundColor: 'var(--bg-secondary, #f5f5f5)',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.4rem',
        }}>
          {/* Row 1: progress dots + status text | Reset (right, always present) */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
              {Array.from({ length: maxStep }).map((_, i) => (
                <span key={i} style={{
                  width: 8, height: 8, borderRadius: '50%',
                  backgroundColor: i < currentStep ? accentColor : 'var(--border-color, #ddd)',
                  transition: 'background-color 0.3s',
                }} />
              ))}
              <span style={{ marginLeft: '8px', fontSize: '0.78rem', color: 'var(--text-secondary, #888)', whiteSpace: 'nowrap' }}>
                {isIdle ? 'Ready' : isRunning ? 'Running…' : isPaused ? `Step ${currentStep}/${maxStep}` : 'Complete'}
              </span>
            </div>
            <button
              onClick={handleReset}
              disabled={isIdle}
              title="Reset"
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '0.28rem 0.6rem', fontSize: '0.78rem', fontWeight: 500,
                border: '1px solid var(--border-color, #ddd)',
                borderRadius: '6px', backgroundColor: 'transparent',
                color: 'var(--text-secondary, #666)',
                cursor: isIdle ? 'default' : 'pointer',
                opacity: isIdle ? 0.4 : 1,
              }}
            >
              <RiRefreshLine size={13} /> Reset
            </button>
          </div>

          {/* Row 2: ◀ · Play/Pause/Resume · ▶ — always three slots, no shift */}
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', justifyContent: 'center' }}>
            {/* ◀ Back — slot 1, always present */}
            <button
              onClick={handleStepBack}
              disabled={currentStep === 0}
              title="Previous step"
              style={{
                display: 'flex', alignItems: 'center',
                padding: '0.35rem 0.6rem', fontSize: '0.82rem',
                border: '1px solid var(--border-color, #ddd)',
                borderRadius: '6px', backgroundColor: 'transparent',
                color: 'var(--text-secondary, #666)',
                cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                opacity: currentStep === 0 ? 0.4 : 1,
              }}
            >
              <RiSkipBackLine size={14} />
            </button>

            {/* Centre button — slot 2, label/style change only, no position change */}
            {isRunning ? (
              <button
                onClick={handlePause}
                title="Pause"
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  padding: '0.35rem 0.85rem', fontSize: '0.82rem', fontWeight: 500,
                  border: `1px solid ${accentColor}`,
                  borderRadius: '6px', backgroundColor: 'transparent',
                  color: accentColor, cursor: 'pointer', minWidth: '4.5rem',
                }}
              >
                <RiPauseLine size={14} /> Pause
              </button>
            ) : isPaused ? (
              <button
                onClick={handleResume}
                title="Resume"
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  padding: '0.35rem 0.85rem', fontSize: '0.82rem', fontWeight: 600,
                  border: `1px solid ${accentColor}`,
                  borderRadius: '6px', backgroundColor: accentColor,
                  color: '#fff', cursor: 'pointer', minWidth: '4.5rem',
                }}
              >
                <RiPlayCircleLine size={14} /> Resume
              </button>
            ) : (
              <button
                onClick={handlePlay}
                title={isDone ? 'Replay' : 'Start'}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  padding: '0.35rem 0.85rem', fontSize: '0.82rem', fontWeight: 700,
                  border: `1px solid ${accentColor}`,
                  borderRadius: '6px', backgroundColor: accentColor,
                  color: '#fff', cursor: 'pointer', minWidth: '4.5rem',
                  transition: 'all 0.18s',
                }}
              >
                <RiPlayCircleLine size={15} />
                {isDone ? 'Replay' : 'Run'}
              </button>
            )}

            {/* ▶ Forward — slot 3, always present */}
            <button
              onClick={handleStepForward}
              disabled={currentStep >= maxStep}
              title="Next step"
              style={{
                display: 'flex', alignItems: 'center',
                padding: '0.35rem 0.6rem', fontSize: '0.82rem',
                border: '1px solid var(--border-color, #ddd)',
                borderRadius: '6px', backgroundColor: 'transparent',
                color: 'var(--text-secondary, #666)',
                cursor: currentStep >= maxStep ? 'not-allowed' : 'pointer',
                opacity: currentStep >= maxStep ? 0.4 : 1,
              }}
            >
              <RiSkipForwardLine size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Explainer section */}
      <div style={{
        marginTop: '2rem',
        padding: '1rem 1.25rem',
        border: '1px solid var(--border-color, #ddd)',
        borderRadius: '12px',
        backgroundColor: 'var(--bg-secondary, #fafafa)',
      }}>
        <div style={{
          fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.25rem',
          color: accentColor,
          display: 'flex', alignItems: 'center', gap: '6px',
        }}>
          {activeTab === 'ask'
            ? <><RiStackLine size={17} /> Ask Mode — Agents &amp; Controls Guide</>
            : activeTab === 'swarm'
            ? <><RiTeamLine size={17} /> Swarm Mode — Controls Guide</>
            : <><RiFlowChart size={17} /> Workflow Mode — How It Works</>
          }
        </div>
        <p style={{ fontSize: '0.83rem', color: 'var(--text-color)', margin: '0 0 0.5rem' }}>
          {activeTab === 'ask'
            ? 'Everything you see in the simulation above, explained.'
            : activeTab === 'swarm'
            ? 'Every button and control available during a Swarm session, explained.'
            : 'Platform workflows, building your own, and running them from the chat panel.'
          }
        </p>
        <hr style={{ margin: '0.75rem 0', borderColor: 'var(--border-color, #ddd)' }} />
        {activeTab === 'ask'
          ? <AskExplainer />
          : activeTab === 'swarm'
          ? <SwarmExplainer />
          : <WorkflowExplainer />
        }
      </div>

      <p style={{ marginTop: '1.25rem', fontSize: '0.85rem', color: 'var(--text-secondary, #888)', fontStyle: 'italic' }}>
        {activeTab === 'workflow'
          ? 'Select a workflow from the ⚙ dropdown above the chat input to get started.'
          : 'Ready to try it for real? Type any question in the input bar and press Send.'
        }
      </p>

      {(onDismiss || onRestore) && (
        <div style={{
          position: 'sticky',
          bottom: 0,
          marginTop: '0.75rem',
          padding: '0.5rem 0',
          display: 'flex',
          justifyContent: 'center',
        }}>
          <label style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            cursor: 'pointer',
            padding: '5px 16px',
            borderRadius: '20px',
            border: '1px solid var(--border-color, #ddd)',
            background: 'var(--bg-secondary, #f5f5f5)',
            fontSize: '0.8rem',
            color: 'var(--text-secondary, #888)',
            userSelect: 'none',
          }}>
            <input
              type="checkbox"
              checked={showAtStartup ?? true}
              onChange={(e) => {
                if (e.target.checked) {
                  onRestore && onRestore();
                } else {
                  onDismiss && onDismiss();
                }
              }}
              style={{ cursor: 'pointer', accentColor: 'var(--primary-color, #636cff)', width: '14px', height: '14px' }}
            />
            Show at startup
          </label>
        </div>
      )}
    </div>
  );
};

export default TutorialContent;
